import React, { useMemo } from 'react';
import { Box, Typography } from '@mui/material';
import { ChartData, ChartOptions, ScriptableContext } from 'chart.js';
import { LoadingSection } from 'components';
import { LineBarChart } from 'dashboard409a/components';
import { ALEXANDRIA_METRICS, MetricUnitsByKey } from 'dashboard409a/services/alexandria/types';
import { compactFormatterNumber, formatMD } from 'dashboard409a/utilities';
import { latestPoint, MetricSeriesByCompany } from './compute/metrics';
import { CompRef } from './constants';

interface CurrentValueBarsProps {
  comps: CompRef[];
  byCompany: MetricSeriesByCompany;
  companyNames: Record<string, string>;
  isLoading: boolean;
  metricUnits: MetricUnitsByKey;
}

interface BarSpec {
  title: string;
  metricKey: string;
  color: string;
  colorTop: string;
}

const BAR_SPECS: BarSpec[] = [
  {
    title: 'Revenue',
    metricKey: ALEXANDRIA_METRICS.LTM_REVENUE,
    color: '#4A3AFF',
    colorTop: '#19C2E7',
  },
  {
    title: 'Enterprise Value',
    metricKey: ALEXANDRIA_METRICS.ENTERPRISE_VALUE,
    color: '#49AA75',
    colorTop: '#6BDCB3',
  },
  {
    title: 'Gross Margin',
    metricKey: ALEXANDRIA_METRICS.GROSS_MARGIN,
    color: '#004A8B',
    colorTop: '#6BDCB3',
  },
];

// Display format from the catalog's unit semantics. factor converts a stored
// value to its absolute quantity, so ratios render as value*factor*100 percent
// and currency as value*factor dollars (compact "$38.2B" style).
const formatterFor = (metricUnits: MetricUnitsByKey, metricKey: string): ((value: number) => string) => {
  const units = metricUnits[metricKey];
  const factor = units?.factor ?? 1;
  if (units?.unit === 'ratio') {
    return value => `${(value * factor * 100).toFixed(1)}%`;
  }
  return value => `$${compactFormatterNumber.format(value * factor)}`;
};

// colorTop at each bar's top, darkening over the full chart height — short
// bars stay light, only tall bars reach the dark base color.
const barGradient =
  (spec: BarSpec) =>
    (context: ScriptableContext<'bar'>): CanvasGradient | string => {
      const { ctx, chartArea, scales } = context.chart;
      const { y: value } = context.parsed ?? {};
      if (!chartArea || !scales.y || typeof value !== 'number') {
        return spec.color;
      }
      const top = scales.y.getPixelForValue(value);
      const gradient = ctx.createLinearGradient(0, top, 0, top + (chartArea.bottom - chartArea.top));
      gradient.addColorStop(0, spec.colorTop);
      gradient.addColorStop(1, spec.color);
      return gradient;
    };

interface BarMeta {
  ticker: string;
  // Observation date when the value was carried forward from an earlier day.
  asOf?: string;
}

// meta parallels the bar data indexes so the tooltip can show it. Hover and
// tooltip behavior are stock Chart.js — only the visuals are styled.
const buildBarOptions = (formatValue: (value: number) => string, meta: BarMeta[]): ChartOptions<'bar'> => ({
  responsive: true,
  maintainAspectRatio: false,
  animation: false,
  plugins: {
    legend: { display: false },
    tooltip: {
      backgroundColor: '#FFFFFF',
      titleColor: '#1F2937',
      titleFont: { weight: 'bold', size: 13 },
      bodyColor: '#4B5563',
      borderColor: 'rgba(0, 0, 0, 0.08)',
      borderWidth: 1,
      cornerRadius: 8,
      padding: 10,
      displayColors: false,
      callbacks: {
        label: context => {
          const { ticker, asOf } = meta[context.dataIndex] ?? {};
          const lines = [ticker, formatValue(context.parsed.y)];
          if (asOf) {
            lines.push(`as of ${formatMD({ date: asOf })}`);
          }
          return lines.filter(Boolean) as string[];
        },
      },
    },
  },
  scales: {
    x: {
      // No axis labels — identity is in the tooltip; the table is the text fallback.
      border: { display: false },
      grid: { color: '#EEF1F5' },
      ticks: { display: false },
    },
    y: {
      border: { display: false, dash: [4, 4] },
      grid: { color: '#E5E7EB' },
      ticks: { color: '#9CA3AF', maxTicksLimit: 5, callback: value => formatValue(Number(value)) },
    },
  },
});

// Latest value per company for revenue, EV, and gross margin — the three
// bar panels under the time-series charts.
const CurrentValueBars = ({ comps, byCompany, companyNames, isLoading, metricUnits }: CurrentValueBarsProps) => {
  const charts = useMemo(
    () =>
      BAR_SPECS.map(spec => {
        const entries = comps
          .map(comp => {
            const point = latestPoint(byCompany[comp.ticker]?.[spec.metricKey]);
            return {
              label: companyNames[comp.ticker] ?? comp.label,
              ticker: comp.ticker,
              value: point ? point.value : null,
              asOf: point?.asOfDate,
            };
          })
          .filter(entry => entry.value !== null)
          // Ascending per chart, so the low and high ends of the range stand out.
          .sort((a, b) => a.value! - b.value!);

        const data: ChartData<'bar'> = {
          labels: entries.map(entry => entry.label),
          datasets: [
            {
              data: entries.map(entry => entry.value!),
              backgroundColor: barGradient(spec),
              borderRadius: 3,
              maxBarThickness: 40,
            },
          ],
        };
        return {
          spec,
          data,
          options: buildBarOptions(
            formatterFor(metricUnits, spec.metricKey),
            entries.map(entry => ({ ticker: entry.ticker, asOf: entry.asOf }))
          ),
        };
      }),
    [byCompany, comps, companyNames, metricUnits]
  );

  return (
    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 3 }}>
      {charts.map(({ spec, data, options }) => (
        <Box key={spec.title} sx={{ flex: '1 1 360px', minWidth: 340 }}>
          <Typography sx={{ color: 'text.primary', fontSize: '18px', fontWeight: 700, mb: 2 }} variant="subtitle2">
            {spec.title}
          </Typography>
          <Box sx={{ height: 320 }}>
            {isLoading ? <LoadingSection /> : <LineBarChart type="bar" data={data} options={options} />}
          </Box>
        </Box>
      ))}
    </Box>
  );
};

export default CurrentValueBars;
